import React, { Component } from 'react';
import ReactPaginate from 'react-paginate';
import { Link } from 'react-router-dom';
import '../Pagination.css';
import '../common/MdPage.css';
import * as constantClass from '../fileWithConstants';
import { createPagedGridController } from '../../hooks/pagedGridClassAdapter';
import * as materiasService from '../../services/materias/materiasService';
import { classifyMateriasError } from '../../services/materias/materiasDto';
import { ConfirmDialog } from '../common/ConfirmDialog';
import {
  buildStandardRowActions,
  MdRowActions,
  MdTableActionsCell,
  MdTableCell,
} from '../common/MdRowActions';
import { PlusIcon, SearchIcon } from '../common/mdPageIcons';

function formatDictado(dictado) {
  if (dictado === '1') return '1° Cuatrimestre';
  if (dictado === '2') return '2° Cuatrimestre';
  return 'Anual';
}

export class MDMateriaCarrera extends Component {
  static displayName = MDMateriaCarrera.name;

  constructor(props) {
    super(props);
    this.state = {
      loading: true,
      searchInput: '',
      perPage: 10,
      paginationData: [],
      pageCount: 0,
      forcePage: 0,
      showErrorMessage: false,
      errorMessage: '',
      successMessage: '',
      deletingId: null,
      pendingDelete: null,
    };
    this.grid = createPagedGridController({
      fetchPage: (query) => materiasService.listMaterias(query),
      defaultPageSize: 10,
      onChange: (gridState) => this.applyGridState(gridState),
    });
    this.searchInputRef = React.createRef();
  }

  applyGridState(gridState) {
    this.setState({
      paginationData: gridState.items,
      pageCount: gridState.pageCount,
      searchInput: gridState.search,
      perPage: gridState.pageSize,
      loading: gridState.loading,
      forcePage: gridState.forcePage,
    });
  }

  async componentDidMount() {
    const canAccess = await constantClass.canAccess('WWMaterias');
    if (canAccess) {
      this.populateData();
    }
  }

  handlePageClick = (e) => {
    this.grid.handlePageClick(e).catch((error) => this.handleMateriasError(error));
  };

  handleSearchInputChange = (e) => {
    const searchInput = e.target.value;
    const cleared = searchInput.trim() === '';
    this.setState({ searchInput, successMessage: '', showErrorMessage: false, errorMessage: '' });
    if (cleared && this.grid.getState().search.trim() !== '') {
      this.grid.clearSearchIfNeeded(this.grid.getState().search).catch((error) => this.handleMateriasError(error));
    }
  };

  handleSearchSubmit = (e) => {
    e.preventDefault();
    const search = (this.searchInputRef.current
      ? this.searchInputRef.current.value
      : this.state.searchInput).trim();
    this.setState({ searchInput: search });
    this.grid.submitSearch(search).catch((error) => this.handleMateriasError(error));
  };

  handleRemoveItem = (materia) => {
    if (this.state.deletingId) {
      return;
    }
    const id = materia.materiaCarreraCod.trim();
    this.setState({
      pendingDelete: {
        id,
        nombre: (materia.materiaCarreraNombre || '').trim(),
        codigo: (materia.materiaCarreraCodigo || '').trim(),
        carrera: (materia.carreraNombre || '').trim(),
      },
    });
  };

  handleCancelDelete = () => {
    if (this.state.deletingId) {
      return;
    }
    this.setState({ pendingDelete: null });
  };

  handleConfirmDelete = () => {
    const { pendingDelete } = this.state;
    if (!pendingDelete || this.state.deletingId) {
      return;
    }
    this.deleteItem(pendingDelete.id);
  };

  renderTable(items) {
    return (
      <div className="md-page__table-card">
        <table className="md-page__table md-page__table--actions-fixed md-page__table--wide" aria-labelledby="md-mxc-title">
          <thead>
            <tr>
              <th scope="col" className="md-page__table__col-dni">Código</th>
              <th scope="col">Nombre</th>
              <th scope="col">Carrera</th>
              <th scope="col" className="md-page__table__col-curso">Curso</th>
              <th scope="col">Régimen</th>
              <th scope="col" className="md-page__table__actions-header" aria-label="Acciones" />
            </tr>
          </thead>
          <tbody>
            {items.map((materia) => {
              const id = materia.materiaCarreraCod.trim();
              return (
                <tr key={id}>
                  <MdTableCell className="md-page__table__col-dni">{materia.materiaCarreraCodigo}</MdTableCell>
                  <MdTableCell>{materia.materiaCarreraNombre}</MdTableCell>
                  <MdTableCell>{materia.carreraNombre}</MdTableCell>
                  <MdTableCell className="md-page__table__col-curso">{materia.materiaCarreraAnio}</MdTableCell>
                  <MdTableCell>{formatDictado(materia.materiaCarreraDictado)}</MdTableCell>
                  <MdTableActionsCell>
                    <MdRowActions
                      items={buildStandardRowActions({
                        editTo: '/update-mxc/' + id + '/0',
                        onDelete: () => this.handleRemoveItem(materia),
                        deleteDisabled: this.state.loading || this.state.deletingId === id,
                        deleteLabel: this.state.deletingId === id ? 'Eliminando...' : 'Borrar',
                        detailTo: '/update-mxc/' + id + '/1',
                      })}
                    />
                  </MdTableActionsCell>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    );
  }

  render() {
    const isBusy = this.state.loading;
    const listLoading = this.state.loading && !this.state.deletingId;
    const { pendingDelete } = this.state;

    const contents = listLoading
      ? <p className="md-page__loading"><em>Cargando datos...</em></p>
      : this.renderTable(this.state.paginationData);

    return (
      <div className="md-page md-mxc">
        <div className="md-page__header">
          <h1 id="md-mxc-title" className="md-page__title">Manejar Datos de Materias</h1>
        </div>

        {!this.state.showErrorMessage ? (
          <div className="md-page__toolbar">
            <Link className="md-page__action-link" to="/add-mxc">
              <PlusIcon />
              Nueva Materia
            </Link>
          </div>
        ) : null}

        <div className="md-page__feedback" aria-live="polite" role="status">
          {this.state.showErrorMessage ? (
            <div className="md-page__error" role="alert">{this.state.errorMessage}</div>
          ) : null}
          {this.state.successMessage ? (
            <div className="md-page__success">{this.state.successMessage}</div>
          ) : null}
        </div>

        {!this.state.showErrorMessage ? (
          <>
            <form className="md-page__search-row" onSubmit={this.handleSearchSubmit}>
              <div className="md-page__search-wrap">
                <span className="md-page__search-icon">
                  <SearchIcon />
                </span>
                <input
                  ref={this.searchInputRef}
                  className="md-page__search-input"
                  type="text"
                  name="search"
                  autoComplete="off"
                  disabled={isBusy}
                  placeholder="Ingrese carrera, materia o código..."
                  value={this.state.searchInput}
                  onChange={this.handleSearchInputChange}
                />
              </div>
              <button type="submit" className="md-page__search-btn" disabled={isBusy}>
                Buscar
              </button>
            </form>

            {contents}

            {!listLoading && this.state.pageCount > 1 ? (
              <div className="md-page__pagination">
                <ReactPaginate
                  previousLabel="Anterior"
                  nextLabel="Siguiente"
                  breakLabel="..."
                  breakClassName="break-me"
                  pageCount={this.state.pageCount}
                  marginPagesDisplayed={2}
                  pageRangeDisplayed={5}
                  onPageChange={this.handlePageClick}
                  forcePage={this.state.forcePage}
                  containerClassName="pagination"
                  subContainerClassName="pages pagination"
                  activeClassName="active"
                />
              </div>
            ) : null}
          </>
        ) : null}

        <ConfirmDialog
          open={Boolean(pendingDelete)}
          title="Eliminar materia"
          message="Esta acción eliminará la materia y no se puede deshacer."
          detail={
            pendingDelete
              ? `${pendingDelete.codigo} — ${pendingDelete.nombre} (${pendingDelete.carrera})`
              : ''
          }
          confirmLabel="Eliminar"
          cancelLabel="Cancelar"
          variant="danger"
          loading={Boolean(this.state.deletingId)}
          onConfirm={this.handleConfirmDelete}
          onCancel={this.handleCancelDelete}
        />
      </div>
    );
  }

  handleMateriasError(error) {
    const outcome = classifyMateriasError(error);
    if (outcome.type === 'timeout') {
      constantClass.RedirectToLogin();
      return;
    }
    this.setState({
      showErrorMessage: true,
      errorMessage: outcome.message,
      loading: false,
    });
  }

  async populateData() {
    try {
      await this.grid.load();
    } catch (error) {
      this.handleMateriasError(error);
    }
  }

  async deleteItem(id) {
    this.setState({ deletingId: id, showErrorMessage: false, errorMessage: '' });
    try {
      await materiasService.deleteMateria(id);
      this.setState({ successMessage: 'El registro se eliminó correctamente.', pendingDelete: null });
      await this.grid.reloadAfterDelete(this.state.paginationData.length);
    } catch (error) {
      this.handleMateriasError(error);
    } finally {
      this.setState({ deletingId: null });
    }
  }
}
